import moment from 'moment';
import { string } from 'yup';

import { ATTRIBUTE_TYPE_VALUES, ATTRIBUTE_TYPES } from '../constants';
import { TAttributeDictionary, TAttributeType } from '../types';
import { IAttribute, IAttributeDefinition } from '../state';
import { TSystemObject } from '../../../state/types';
import TranslationHelper from '../../../helpers/TranslationHelper';

export const getAttributesPayload = (attributes: TAttributeDictionary) =>
    Object.values(attributes).flatMap((attr) => {
        const { connectedToDictionary, name, value } = attr;
        if (value === null || value === '' || value === undefined) {
            return [];
        }
        return [
            {
                connectedToDictionary,
                name,
                value: connectedToDictionary
                    ? value
                    : getValue(value, attr.type),
            },
        ];
    });

export const getValue = (
    value: string | number | boolean,
    type: TAttributeType
) => {
    const attrTypeName = ATTRIBUTE_TYPES[type];

    switch (attrTypeName) {
        case ATTRIBUTE_TYPE_VALUES.Boolean:
            return String(value) === 'true';
        case ATTRIBUTE_TYPE_VALUES.Integer:
        case ATTRIBUTE_TYPE_VALUES.Double:
            return Number(value);

        case ATTRIBUTE_TYPE_VALUES.Time:
            return moment(value as string, 'HH:mm:ss').format('HH:mm:ss');

        case ATTRIBUTE_TYPE_VALUES.Date:
            return moment(value as string).format('YYYY-MM-DD');

        default:
            return value;
    }
};

export const getAttributeValue = (attribute: IAttribute, attrValue?: any) => {
    const attrTypeName = ATTRIBUTE_TYPES[attribute.definition.type];

    switch (attrTypeName) {
        case ATTRIBUTE_TYPE_VALUES.Boolean:
            return attrValue !== undefined ? attrValue : '';

        case ATTRIBUTE_TYPE_VALUES.Time:
        case ATTRIBUTE_TYPE_VALUES.Date:
            return attrValue || '';

        default:
            return attrValue || '';
    }
};

export const prepareAttributesFormData = (
    attributes: IAttribute[],
    type: TSystemObject,
    dataAttributes: Record<string, any>
): Record<string, IAttributeDefinition> => {
    return attributes
        .filter((attribute) => {
            const type = ATTRIBUTE_TYPES[attribute.definition.type];
            return (
                type !== 'File' &&
                !attribute.connectedTo.some((c) => c.multipleValue)
            );
        })
        .reduce<Record<string, any>>((acc, attribute) => {
            const objectTypeData = attribute.connectedTo.find(
                (typeSpecificData) => typeSpecificData.type === type
            );

            acc[attribute.definition.name] = {
                label: attribute.definition.label,
                name: attribute.definition.name,
                connectedToDictionary:
                    attribute.definition.lockedToDictionary || false,
                value: getAttributeValue(
                    attribute,
                    dataAttributes[attribute.definition.name]
                ),
                type: attribute.definition.type,
                required: objectTypeData?.required || false,
                regex: attribute.definition.regex,
                dictionary: attribute.dictionary,
                internal: attribute.definition.internal,
                validation: objectTypeData?.required
                    ? string()
                          .nullable()
                          .required(
                              TranslationHelper.translate('Field is required')
                          )
                    : string().nullable(),
            };

            return acc;
        }, {});
};
